import { useMutation, useQueryClient } from '@tanstack/react-query'
import { userApi } from '../api/userApi'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { supabaseAuth } from '@/features/auth/api/supabaseAuth'
import { toast } from '@/hooks/use-toast'

export const useProfile = () => {
  const { user, setUser } = useAuthStore()
  const queryClient = useQueryClient()

  const updateProfileMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: any }) =>
      userApi.updateUser(userId, data),
    onSuccess: (updatedUser) => {
      setUser(updatedUser)
      queryClient.invalidateQueries({ queryKey: ['user', updatedUser.id] })
      toast({
        title: 'Profile updated',
        description: 'Your profile has been updated successfully.',
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error updating profile',
        description: error.response?.data?.message || 'Failed to update profile.',
        variant: 'destructive',
      })
    },
  })

  const updatePasswordMutation = useMutation({
    mutationFn: async ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string
      newPassword: string
    }) => {
      const result = await userApi.updatePassword(currentPassword, newPassword)
      // The login tries Supabase first, so a linked artist's old password would keep working
      // there. The server syncs it when it can; when it could not, and this browser holds a
      // Supabase session, update it from here. Best-effort — the change itself has succeeded.
      // Never while masquerading: that browser's Supabase session is the manager's, not the
      // artist's (updatePasswordIfSignedIn also checks the session email for the same reason).
      const { masquerade } = useAuthStore.getState()
      if (result.supabase !== 'synced' && !masquerade) {
        await supabaseAuth.updatePasswordIfSignedIn(newPassword, user?.email)
      }
      return result
    },
    onSuccess: () => {
      toast({
        title: 'Password updated',
        description: 'Your password has been changed successfully.',
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error updating password',
        description: error.response?.data?.message || 'Failed to update password.',
        variant: 'destructive',
      })
    },
  })

  const uploadProfileImageMutation = useMutation({
    mutationFn: ({ userId, file }: { userId: number; file: File }) =>
      userApi.uploadProfileImage(userId, file),
    onSuccess: (updatedUser) => {
      setUser(updatedUser)
      queryClient.invalidateQueries({ queryKey: ['user', updatedUser.id] })
      toast({
        title: 'Profile picture updated',
        description: 'Your profile picture has been uploaded successfully.',
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error uploading image',
        description: error.response?.data?.message || 'Failed to upload profile picture.',
        variant: 'destructive',
      })
    },
  })

  return {
    user,
    updateProfile: updateProfileMutation.mutate,
    isUpdatingProfile: updateProfileMutation.isPending,
    /** Resolves on success and rejects on failure (after the error toast), so the form can
     *  keep what was typed when the change did not happen. */
    updatePassword: updatePasswordMutation.mutateAsync,
    isUpdatingPassword: updatePasswordMutation.isPending,
    uploadProfileImage: uploadProfileImageMutation.mutate,
    isUploadingImage: uploadProfileImageMutation.isPending,
  }
}

